import { lazy } from 'react';

import { Codeowners } from '../consts/codeowners';

import { captureException } from './exception/captureException';
import { localStorage } from './storage';

const PAGE_HAS_BEEN_FORCE_REFRESHED = 'page-has-been-force-refreshed';

export const isIOSSafari = () => {
  const userAgent = navigator?.userAgent;
  return /iPad|iPhone|iPod/.test(userAgent) && /Safari/.test(userAgent) && !/CriOS|FxiOS|OPiOS|mercury/.test(userAgent);
};

const isLocalStorageAvailable = () => {
  try {
    const test = '__localStorage_test__';
    localStorage.setItem(test, test);
    localStorage.removeItem(test);
    return true;
  } catch {
    return false;
  }
};

// iOS Safari has a specific issue with lazy loading, so we need to handle it differently
const safariImport = async (importFn) => {
  const timeout = 5000;
  return Promise.race([
    importFn(),
    new Promise((_, reject) => {
      setTimeout(() => reject(new Error('iOS Safari import timeout')), timeout);
    }),
  ]);
};

const lazyWithRetry = (componentImport) => {
  return lazy(async () => {
    // Only use refresh mechanism if localStorage is available
    const localStorageAvailable = isLocalStorageAvailable();
    const pageHasBeenForceRefreshed = localStorageAvailable
      ? JSON.parse(localStorage.getItem(PAGE_HAS_BEEN_FORCE_REFRESHED) || 'false')
      : false;

    try {
      const component = isIOSSafari() ? await safariImport(componentImport) : await componentImport();

      if (!component || !component.default) {
        const error = new Error('Dynamic import returned undefined module');
        error.isModuleUndefined = true;
        throw error;
      }

      if (localStorageAvailable) {
        localStorage.setItem(PAGE_HAS_BEEN_FORCE_REFRESHED, 'false');
      }
      return component;
    } catch (error) {
      captureException(error, Codeowners.CUSTOMER_INTAKE, {
        'lazy-loading': true,
        'ios-safari': isIOSSafari(),
        'page-refreshed': pageHasBeenForceRefreshed,
        'module-undefined': error.isModuleUndefined || false,
        'error-type': error.message || 'unknown',
        'localStorage-available': localStorageAvailable,
      });

      if (localStorageAvailable && !pageHasBeenForceRefreshed) {
        localStorage.setItem(PAGE_HAS_BEEN_FORCE_REFRESHED, 'true');
        // eslint-disable-next-line no-restricted-properties
        window.location.reload();
        return new Promise(() => {});
      }

      throw error;
    }
  });
};

export default lazyWithRetry;
